import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday, getDay, startOfWeek, addDays } from 'date-fns';
import { X, Trash2, Clock, MapPin, User, Users, GitBranch, Palette, RefreshCw, CalendarCheck, BookOpen, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { COLOR_MAP, getColor } from './CalendarGrid';
import { supabase } from '../../lib/supabase';

const COLORS = ['blue', 'violet', 'rose', 'emerald', 'orange', 'amber', 'indigo', 'teal', 'pink', 'slate', 'cyan', 'lime', 'yellow', 'red', 'purple', 'fuchsia', 'green'];
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'yearly', label: 'Every year' },
];

const EVENT_TYPE_TABS = [
  { id: 'lecture', label: 'Lecture' },
  { id: 'test', label: 'Test' },
  { id: 'availability', label: 'Availability' },
  { id: 'normal', label: 'Normal' },
];

const AVAIL_SUBTABS = [
  { id: 'faculty', label: 'Faculty' },
  { id: 'batch', label: 'Batch' },
  { id: 'holiday', label: 'Holiday' },
];

function toLocalStr(dt) {
  if (!dt) return '';
  const d = typeof dt === 'string' ? parseISO(dt) : dt;
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

function _rruleToType(rrule) {
  if (!rrule) return 'none';
  if (rrule.includes('DAILY')) return 'daily';
  if (rrule.includes('WEEKLY')) return 'weekly';
  if (rrule.includes('MONTHLY')) return 'monthly';
  if (rrule.includes('YEARLY')) return 'yearly';
  return 'none';
}

/* ─── Segmented Control ────────────────────────────── */
function SegmentedControl({ options, value, onChange, className }) {
  return (
    <div className={cn("flex bg-zinc-800/60 rounded-full p-1 gap-0.5", className)}>
      {options.map(opt => (
        <button
          key={opt.id}
          type="button"
          className={cn(
            "flex-1 text-xs font-semibold py-1.5 px-3 rounded-full transition-all duration-150 whitespace-nowrap",
            value === opt.id
              ? "bg-blue-600 text-white shadow-md"
              : "text-zinc-400 hover:text-zinc-200"
          )}
          onClick={() => onChange(opt.id)}
          data-testid={`tab-${opt.id}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Main Modal ───────────────────────────────────── */
export default function EventModal({ event, isOpen, onClose, onSave, onDelete, onSelect, faculty, batches, subjects, branches, assignments }) {
  const isEdit = !!event?.id && !event?.id?.startsWith?.('temp_');

  // Primary state
  const [eventType, setEventType] = useState('lecture');
  const [availSubTab, setAvailSubTab] = useState('faculty'); // faculty | batch | holiday


  const [form, setForm] = useState({
    title: '', start_time: '', end_time: '', description: '', location: '',
    color: 'blue', recurrence: 'none', faculty_id: '', batch_id: '', branch_id: '', room: '',
    is_all_day: false, assignment_id: '', test_id: '', attendance_id: '', subject_id: '',
    // Availability-specific
    entity_type: '', entity_id: '', availability_mode: 'preferred',
    duplicateDates: [], // ISO date strings e.g. ['2024-06-03']
  });

  // Bidirectional filter: filter assignments by currently selected faculty/batch/branch
  const filteredAssignments = useMemo(() => {
    if (!assignments?.length) return [];
    return assignments.filter(a => {
      if (form.faculty_id && a.faculty_id !== form.faculty_id) return false;
      if (form.batch_id && a.batch_id !== form.batch_id) return false;
      if (form.branch_id) {
        const batch = batches?.find(b => b.id === a.batch_id);
        if (batch && batch.branch_id !== form.branch_id) return false;
      }
      return true;
    });
  }, [assignments, batches, form.faculty_id, form.batch_id, form.branch_id]);

  // Handle assignment selection: auto-fill faculty, batch, branch, title
  const handleAssignmentSelect = (assignmentId) => {
    if (!assignmentId) {
      setForm(f => ({ ...f, assignment_id: '' }));
      return;
    }
    const asg = assignments?.find(a => a.id === assignmentId);
    if (!asg) return;
    const batch = batches?.find(b => b.id === asg.batch_id);
    onSelect?.(asg, 'assignment');
    setForm(f => ({
      ...f,
      assignment_id: assignmentId,
      faculty_id: asg.faculty_id || '',
      batch_id: asg.batch_id || '',
      subject_id: asg.subject_id || '',
      branch_id: batch?.branch_id || f.branch_id || '',
      title: asg.subject_name || f.title,
      color: asg.color || f.color || 'blue',
    }));
  };
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  


  useEffect(() => {
    if (!isOpen) { setDeleteConfirm(false); return; }
    if (event) {
      const et = event.event_type || 'lecture';
      setEventType(et === 'holiday' ? 'availability' : et);
      if (et === 'holiday') setAvailSubTab('holiday');
      else if (et === 'availability') {
        setAvailSubTab(event.entity_type === 'batch' ? 'batch' : 'faculty');
      } else {
        setAvailSubTab('faculty');
      }
      
      const resolvedBatchIds = new Set();
      if (event.batch_id) resolvedBatchIds.add(String(event.batch_id));
      if (event.calendar_ids && Array.isArray(event.calendar_ids)) {
        event.calendar_ids.forEach(cid => {
          if (typeof cid === 'string' && cid.startsWith('batch_')) {
            resolvedBatchIds.add(cid.substring(6));
          }
        });
      }
      
      setForm({
        title: event.title || '',
        start_time: toLocalStr(event.start_time) || '',
        end_time: toLocalStr(event.end_time) || '',
        description: event.description || '',
        location: event.location || '',
        color: event.color || 'blue',
        recurrence: event.recurrence_type || _rruleToType(event.recurrence_rule) || 'none',
        faculty_id: event.faculty_id || '',
        batch_id: event.batch_id || '',
        batch_ids: Array.from(resolvedBatchIds),
        branch_id: event.branch_id || '',
        room: event.room || '',
        is_all_day: event.is_all_day || false,
        assignment_id: event.assignment_id || '',
        test_id: event.test_id || '',
        attendance_id: event.attendance_id || '',
        entity_type: event.entity_type || '',
        entity_id: event.entity_id || '',
        availability_mode: event.availability_mode || 'preferred',
        subject_id: event.subject_id || '',
        duplicateDates: [],
      });
    } else {
      setEventType('lecture');
      setAvailSubTab('faculty');
      setForm({
        title: '', start_time: '', end_time: '', description: '', location: '',
        color: 'blue', recurrence: 'none', faculty_id: '', batch_id: '', batch_ids: [], branch_id: '', room: '',
        is_all_day: false, assignment_id: '', test_id: '', attendance_id: '', entity_type: '', entity_id: '', availability_mode: 'preferred',
        subject_id: '',
        duplicateDates: [],
      });
    }
    setDeleteConfirm(false);
  }, [event, isOpen]);

  // Derive the actual event_type for the API
  const resolvedEventType = eventType === 'availability' && availSubTab === 'holiday' ? 'holiday' : eventType;



  const handleSave = () => {
    // Validate required fields
    if (resolvedEventType === 'normal' || resolvedEventType === 'lecture') {
      if (!form.title.trim()) {
        toast.error('Title is required');
        return;
      }
    }

    // For availability events, validate entity selection
    if (resolvedEventType === 'availability') {
      const entityId = availSubTab === 'faculty' ? form.faculty_id : form.batch_id;
      if (!entityId) {
        toast.error(`Please select a ${availSubTab} before saving`);
        return;
      }
    }

    // Handle all-day events: generate midnight-to-end-of-day timestamps from date portion
    let startTime = form.start_time;
    let endTime = form.end_time;
    if (form.is_all_day || resolvedEventType === 'holiday') {
      // Extract date portion from start_time (or use as-is if it's date-only)
      const dateStr = (startTime || '').split('T')[0];
      if (dateStr) {
        startTime = `${dateStr}T00:00`;
        endTime = `${dateStr}T23:59:59`;
      }
    }
    if (!startTime || !endTime) {
      toast.error('Start and end times are required');
      return;
    }

    const payload = {
      ...form,
      event_type: resolvedEventType,
      start_time: new Date(startTime).toISOString(),
      end_time: new Date(endTime).toISOString(),
      id: event?.id?.startsWith?.('temp_') ? undefined : event?.id,
      instance_date: event?.instance_date,
      is_recurring_instance: event?.is_recurring_instance,
    };

    // Calculate calendar_ids
    if (resolvedEventType === 'lecture' || resolvedEventType === 'normal' || resolvedEventType === 'test') {
      const calIds = ['main'];
      if (form.faculty_id) calIds.push(`faculty_${form.faculty_id}`);
      if (form.branch_id) calIds.push(`branch_${form.branch_id}`);
      
      // Multi-batch calendar mapping
      if (form.batch_ids && form.batch_ids.length > 0) {
        form.batch_ids.forEach(bid => {
          calIds.push(`batch_${bid}`);
        });
      } else if (form.batch_id) {
        calIds.push(`batch_${form.batch_id}`);
      }
      payload.calendar_ids = Array.from(new Set(calIds));
    }

    // Set type-specific fields
    if (resolvedEventType === 'availability') {
      payload.entity_type = availSubTab; // 'faculty' or 'batch'
      payload.entity_id = availSubTab === 'faculty' ? form.faculty_id : form.batch_id;
      payload.title = payload.title || `${availSubTab === 'faculty' ? 'Faculty' : 'Batch'} - ${form.availability_mode}`;
      // Clear lecture-specific fields
      payload.assignment_id = null;
      payload.subject_id = null;
    } else if (resolvedEventType === 'holiday') {
      payload.is_all_day = true;
      payload.entity_type = form.entity_type || null;
      payload.entity_id = form.entity_id || null;
      payload.availability_mode = null;
      payload.assignment_id = null;
      payload.subject_id = null;
    } else if (resolvedEventType === 'normal') {
      payload.entity_type = null;
      payload.entity_id = null;
      payload.availability_mode = null;
      payload.assignment_id = null;
      payload.subject_id = null;
      payload.test_id = null;
      payload.attendance_id = null;
    } else if (resolvedEventType === 'test') {
      payload.entity_type = null;
      payload.entity_id = null;
      payload.availability_mode = null;
      payload.assignment_id = null;
      payload.attendance_id = null;
    }
    // For 'lecture', all fields pass through as-is

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/35 z-[1000] animate-[fadeIn_0.2s_ease]" onClick={onClose} data-testid="event-modal-overlay" />

      {/* Side Panel */}
      <div className="fixed top-0 right-0 bottom-0 w-[25.0rem] max-w-[100vw] bg-white dark:bg-zinc-800 shadow-[-6px_0_32px_rgba(0,0,0,0.18)] z-[1001] flex flex-col animate-[slideInRight_0.22s_cubic-bezier(0.32,0.72,0,1)]" onClick={e => e.stopPropagation()} data-testid="event-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/50 flex-shrink-0">
          <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{isEdit ? 'Edit Event' : 'New Event'}</h3>
          <button className="w-8 h-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-700 flex items-center justify-center text-zinc-400" onClick={onClose} data-testid="modal-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* ── Type Selector (Segmented Nav) ── */}
          <SegmentedControl
            options={EVENT_TYPE_TABS}
            value={eventType}
            onChange={setEventType}
          />

          {/* ── Availability Sub-tabs ── */}
          {eventType === 'availability' && (
            <SegmentedControl
              options={AVAIL_SUBTABS}
              value={availSubTab}
              onChange={setAvailSubTab}
              className="!bg-zinc-700/40"
            />
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* LECTURE FORM */}
          {/* ═══════════════════════════════════════════ */}
          {eventType === 'lecture' && (
            <>
              {/* Title */}
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Add title" className="h-10 text-base font-medium border-0 border-b-2 border-zinc-200 dark:border-zinc-600 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500" data-testid="event-title-input" autoFocus />

              {/* All Day + Time */}
              <AllDayAndTime form={form} setForm={setForm} />
              <p className="text-[0.6rem] text-zinc-400 ml-7 -mt-2 mb-2 italic">Note: All-day events appear at the top of the calendar grid.</p>

              {/* Recurrence */}
              <RecurrenceField form={form} setForm={setForm} />



              {/* Location / Room */}
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <Input value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))} placeholder="Add location / room" className="h-9 text-xs rounded-lg" data-testid="event-room-input" />
              </div>

              {/* Description */}
              <DescriptionField form={form} setForm={setForm} />

              {/* Assignment Details */}
              <div className="border-t border-zinc-100 dark:border-zinc-700/50 pt-4">
                <p className="text-[0.625rem] uppercase tracking-wider font-semibold text-zinc-400 mb-3">Assignment Details</p>
              </div>

              {/* Faculty Assignment (filtered) */}
              <div className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select value={form.assignment_id || ''} onChange={e => handleAssignmentSelect(e.target.value)} className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" data-testid="event-assignment-select">
                  <option value="">Select assignment</option>
                  {filteredAssignments.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.subject_name} → {a.faculty_name} ({a.batch_name})
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[0.6rem] text-zinc-400 ml-7 -mt-2 mb-2 italic">Linking an assignment automatically sets Subject, Batch, and Faculty.</p>

              {/* Subject */}
              <div className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select 
                  value={form.subject_id || ''} 
                  onChange={e => {
                    const id = e.target.value;
                    setForm(f => ({ ...f, subject_id: id, assignment_id: '' }));
                    const s = subjects?.find(x => x.id === id);
                    if (s) onSelect?.(s, 'subject');
                  }} 
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" 
                  data-testid="event-subject-select"
                >
                  <option value="">No subject</option>
                  {subjects?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <p className="text-[0.6rem] text-zinc-400 ml-7 -mt-2 mb-2 italic">Subject links are used to calculate progress percentages in the Batches panel.</p>

              {/* Batches (Multi-select Group Lecture) */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">Batches (Select multiple for Group Lecture)</span>
                </div>
                <div className="grid grid-cols-2 gap-2 pl-7">
                  {batches?.map(b => {
                    const isSelected = form.batch_ids?.includes(b.id) || String(form.batch_id) === String(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => {
                          let nextBatchIds = form.batch_ids ? [...form.batch_ids] : [];
                          const bId = b.id;
                          if (nextBatchIds.includes(bId)) {
                            nextBatchIds = nextBatchIds.filter(id => id !== bId);
                          } else {
                            nextBatchIds.push(bId);
                          }
                          setForm(f => ({
                            ...f,
                            batch_ids: nextBatchIds,
                            batch_id: nextBatchIds[0] || '',
                            assignment_id: ''
                          }));
                          onSelect?.(b, 'batch');
                        }}
                        className={cn(
                          "flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all duration-150",
                          isSelected
                            ? "bg-blue-600/10 border-blue-500 text-blue-600 dark:text-blue-400 font-semibold"
                            : "bg-zinc-50 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700/60 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/70"
                        )}
                        data-testid={`batch-chip-${b.id}`}
                      >
                        <span className="text-xs truncate mr-2">{b.name}</span>
                        <div className={cn(
                          "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0",
                          isSelected ? "border-blue-500 bg-blue-500 text-white" : "border-zinc-300 dark:border-zinc-600"
                        )}>
                          {isSelected && <svg className="w-2.5 h-2.5 stroke-[3] stroke-white" fill="none" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Faculty */}
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select 
                  value={form.faculty_id || ''} 
                  onChange={e => {
                    const id = e.target.value;
                    setForm(f => ({ ...f, faculty_id: id, assignment_id: '' }));
                    const fac = faculty?.find(x => x.id === id);
                    if (fac) onSelect?.(fac, 'faculty');
                  }} 
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" 
                  data-testid="event-faculty-select"
                >
                  <option value="">No faculty</option>
                  {faculty?.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>

              {/* Branch */}
              <div className="flex items-center gap-3">
                <GitBranch className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select 
                  value={form.branch_id || ''} 
                  onChange={e => {
                    const id = e.target.value;
                    setForm(f => ({ ...f, branch_id: id, assignment_id: '' }));
                    const br = branches?.find(x => x.id === id);
                    if (br) onSelect?.(br, 'branch');
                  }} 
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" 
                  data-testid="event-branch-select"
                >
                  <option value="">No branch</option>
                  {branches?.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              {/* Color */}
              <ColorPicker form={form} setForm={setForm} />
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* TEST FORM */}
          {/* ═══════════════════════════════════════════ */}
          {eventType === 'test' && (
            <>
              {/* Title */}
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Add title" className="h-10 text-base font-medium border-0 border-b-2 border-zinc-200 dark:border-zinc-600 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500" data-testid="event-title-input" autoFocus />

              {/* All Day + Time */}
              <AllDayAndTime form={form} setForm={setForm} />

              {/* Recurrence */}
              <RecurrenceField form={form} setForm={setForm} />



              {/* Subject Selection */}
              <div className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select 
                  value={form.subject_id || ''} 
                  onChange={e => setForm(f => ({ ...f, subject_id: e.target.value }))} 
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" 
                  data-testid="event-subject-select"
                >
                  <option value="">Select subject</option>
                  {subjects?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Batch Selection */}
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <select 
                  value={form.batch_id || ''} 
                  onChange={e => {
                    const id = e.target.value;
                    setForm(f => ({ ...f, batch_id: id }));
                    const b = batches?.find(x => x.id === id);
                    if (b) onSelect?.(b, 'batch');
                  }} 
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" 
                  data-testid="event-batch-select"
                >
                  <option value="">Select batch</option>
                  {batches?.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              {/* Location / Room */}
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <Input value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))} placeholder="Add location / room" className="h-9 text-xs rounded-lg" data-testid="event-room-input" />
              </div>

              {/* Description */}
              <DescriptionField form={form} setForm={setForm} />

              {/* Color */}
              <ColorPicker form={form} setForm={setForm} />
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* NORMAL FORM */}
          {/* ═══════════════════════════════════════════ */}
          {eventType === 'normal' && (
            <>
              {/* Title */}
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Add title" className="h-10 text-base font-medium border-0 border-b-2 border-zinc-200 dark:border-zinc-600 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500" data-testid="event-title-input" autoFocus />

              {/* All Day + Time */}
              <AllDayAndTime form={form} setForm={setForm} />

              {/* Recurrence */}
              <RecurrenceField form={form} setForm={setForm} />

              {/* Location / Room */}
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <Input value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))} placeholder="Add location / room" className="h-9 text-xs rounded-lg" data-testid="event-room-input" />
              </div>

              {/* Description */}
              <DescriptionField form={form} setForm={setForm} />

              {/* Color */}
              <ColorPicker form={form} setForm={setForm} />
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* AVAILABILITY FORM (Faculty / Batch) */}
          {/* ═══════════════════════════════════════════ */}
          {eventType === 'availability' && availSubTab !== 'holiday' && (
            <>
              {/* Entity selector */}
              <div className="flex items-center gap-3">
                {availSubTab === 'faculty' ? (
                  <User className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                ) : (
                  <Users className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                )}
                <select
                  value={(availSubTab === 'faculty' ? form.faculty_id : form.batch_id) || ''}
                  onChange={e => {
                    const id = e.target.value;
                    if (availSubTab === 'faculty') {
                      setForm(f => ({ ...f, faculty_id: id, entity_id: id }));
                      const fac = faculty?.find(x => x.id === id);
                      if (fac) onSelect?.(fac, 'faculty');
                    } else {
                      setForm(f => ({ ...f, batch_id: id, entity_id: id }));
                      const b = batches?.find(x => x.id === id);
                      if (b) onSelect?.(b, 'batch');
                    }
                  }}
                  className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200"
                  data-testid="avail-entity-select"
                >
                  <option value="">No {availSubTab}</option>
                  {availSubTab === 'faculty'
                    ? faculty?.map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                    : batches?.map(b => <option key={b.id} value={b.id}>{b.name}</option>)
                  }
                </select>
              </div>

              {/* Preferred / Unavailable toggle */}
              <SegmentedControl
                options={[
                  { id: 'preferred', label: 'Preferred' },
                  { id: 'unavailable', label: 'Unavailable' },
                ]}
                value={form.availability_mode}
                onChange={mode => setForm(f => ({ ...f, availability_mode: mode, color: mode === 'unavailable' ? 'rose' : 'emerald' }))}
                className="!bg-zinc-700/40"
              />

              {/* All Day + Time */}
              <AllDayAndTime form={form} setForm={setForm} />

              {/* Recurrence */}
              <RecurrenceField form={form} setForm={setForm} />

              {/* Description */}
              <DescriptionField form={form} setForm={setForm} />
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* HOLIDAY FORM */}
          {/* ═══════════════════════════════════════════ */}
          {eventType === 'availability' && availSubTab === 'holiday' && (
            <>
              {/* Holiday Name */}
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Holiday Name" className="h-10 text-base font-medium border-0 border-b-2 border-zinc-200 dark:border-zinc-600 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500" data-testid="event-title-input" autoFocus />

              {/* All Day + Time (All Day locked to true) */}
              <AllDayAndTime form={{ ...form, is_all_day: true }} setForm={setForm} allDayLocked />

              {/* Recurrence */}
              <RecurrenceField form={form} setForm={setForm} />

              {/* Description */}
              <DescriptionField form={form} setForm={setForm} />
            </>
          )}

          {/* Copy to other days — mini calendar picker (only when creating a new event) */}
          {!isEdit && (
            <MiniDatePicker
              selectedDates={form.duplicateDates || []}
              originDate={form.start_time ? parseISO(form.start_time) : null}
              onChange={dates => setForm(f => ({ ...f, duplicateDates: dates }))}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-200 dark:border-zinc-700/50 flex-shrink-0 bg-zinc-50/50 dark:bg-zinc-800/50">
          <div>
            {isEdit && !deleteConfirm && (
              <button className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 border border-red-300/30 rounded-lg px-3 py-1.5" onClick={() => setDeleteConfirm(true)} data-testid="event-delete-btn">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
            {isEdit && deleteConfirm && (
              <div className="flex items-center gap-2">
                {event?.is_recurring_instance ? (
                  <>
                    <button className="text-[0.625rem] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400" onClick={() => { onDelete?.(event, 'single'); setDeleteConfirm(false); }} data-testid="event-delete-single-btn">This event</button>
                    <button className="text-[0.625rem] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400" onClick={() => { onDelete?.(event, 'all'); setDeleteConfirm(false); }} data-testid="event-delete-all-btn">All events</button>
                  </>
                ) : (
                  <button className="text-[0.625rem] px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600" onClick={() => { onDelete?.(event, 'all'); setDeleteConfirm(false); }} data-testid="event-delete-confirm-btn">Confirm Delete</button>
                )}
                <button className="text-[0.625rem] text-zinc-400 hover:text-zinc-600" onClick={() => setDeleteConfirm(false)}>Cancel</button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} data-testid="event-cancel-btn">Cancel</Button>
            <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} data-testid="event-save-btn">Save</Button>
          </div>
        </div>
      </div>
    </>
  );
}


/* ─── Shared Sub-Components ──────────────────────── */

function AllDayAndTime({ form, setForm, allDayLocked }) {
  return (
    <div className="space-y-3">
      {/* All Day checkbox */}
      <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="all-day-toggle">
        <input
          type="checkbox"
          checked={form.is_all_day}
          onChange={e => !allDayLocked && setForm(f => ({ ...f, is_all_day: e.target.checked }))}
          disabled={allDayLocked}
          className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
        />
        <CalendarCheck className="w-3.5 h-3.5 text-zinc-400" />
        <span className="text-xs text-zinc-500 dark:text-zinc-400">All day{allDayLocked ? ' (required)' : ''}</span>
      </label>

      {/* Time inputs (hidden if all-day) */}
      {!form.is_all_day && (
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <div className="flex-1 grid grid-cols-2 gap-2">
            <Input type="datetime-local" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="h-9 text-xs rounded-lg" data-testid="event-start-input" />
            <Input type="datetime-local" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="h-9 text-xs rounded-lg" data-testid="event-end-input" />
          </div>
        </div>
      )}

      {/* Date-only inputs (shown when all-day) */}
      {form.is_all_day && (
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div>
              <label className="text-[0.625rem] text-zinc-400 mb-0.5 block">Start date</label>
              <Input
                type="date"
                value={(form.start_time || '').split('T')[0]}
                onChange={e => setForm(f => ({ ...f, start_time: `${e.target.value}T00:00` }))}
                className="h-9 text-xs rounded-lg"
                data-testid="event-start-date-input"
              />
            </div>
            <div>
              <label className="text-[0.625rem] text-zinc-400 mb-0.5 block">End date</label>
              <Input
                type="date"
                value={(form.end_time || '').split('T')[0]}
                onChange={e => setForm(f => ({ ...f, end_time: `${e.target.value}T23:59` }))}
                className="h-9 text-xs rounded-lg"
                data-testid="event-end-date-input"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecurrenceField({ form, setForm }) {
  return (
    <div className="flex items-center gap-3">
      <RefreshCw className="w-4 h-4 text-zinc-400 flex-shrink-0" />
      <select value={form.recurrence || ''} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))} className="flex-1 h-9 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 text-zinc-800 dark:text-zinc-200" data-testid="event-recurrence-select">
        {RECURRENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function DescriptionField({ form, setForm }) {
  return (
    <div className="flex items-start gap-3">
      <svg className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h10" /></svg>
      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Add description" className="flex-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-3 py-2 text-zinc-800 dark:text-zinc-200 resize-none" data-testid="event-desc-input" />
    </div>
  );
}

function ColorPicker({ form, setForm }) {
  return (
    <div className="space-y-1.5">
      <span className="text-[0.6875rem] font-semibold text-zinc-400">Color</span>
      <div className="flex gap-1.5 flex-wrap">
        {COLORS.map(c => (
          <button key={c} className={cn("w-6 h-6 rounded-full border-2 transition-all", form.color === c ? "border-zinc-800 dark:border-white scale-110 ring-2 ring-white dark:ring-zinc-900" : "border-transparent hover:scale-105")}
            style={{ background: COLOR_MAP[c] || '#666' }} onClick={() => setForm(f => ({ ...f, color: c }))} data-testid={`color-${c}`} />
        ))}
      </div>
    </div>
  );
}

/* ─── Mini Date Picker ───────────────────────────────── */
function MiniDatePicker({ selectedDates, originDate, onChange }) {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(() => originDate || today);

  // Keep viewMonth in sync when originDate changes (e.g. user changes start time)
  useEffect(() => {
    if (originDate) setViewMonth(startOfMonth(originDate));
  }, [originDate?.toDateString()]);

  // Build calendar grid: fill leading/trailing blanks so grid always starts on Sunday
  const monthStart = startOfMonth(viewMonth);
  const monthEnd   = endOfMonth(viewMonth);
  const gridStart  = startOfWeek(monthStart, { weekStartsOn: 0 });
  const allDays    = eachDayOfInterval({ start: gridStart, end: addDays(gridStart, 41) }); // 6 weeks

  const originKey  = originDate ? format(originDate, 'yyyy-MM-dd') : null;
  const selectedSet = new Set(selectedDates);
  const count = selectedDates.length;

  const toggle = (day) => {
    const key = format(day, 'yyyy-MM-dd');
    if (key === originKey) return; // can't deselect origin day marker
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  };

  return (
    <div className="border-t border-zinc-100 dark:border-zinc-700/50 pt-4 space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <span className="text-[0.6875rem] font-semibold text-zinc-400 uppercase tracking-wider">
          Copy to other days
        </span>
        {count > 0 && (
          <span className="text-[0.6875rem] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full leading-none">
            {count} day{count !== 1 ? 's' : ''} selected
          </span>
        )}
      </div>
      <p className="text-[0.6rem] text-zinc-400 italic -mt-1">
        Select any dates below — the event will be copied at the same time on each chosen day.
      </p>

      {/* Month navigation */}
      <div className="bg-zinc-50 dark:bg-zinc-900/60 rounded-xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 dark:border-zinc-700/50">
          <button
            type="button"
            onClick={() => setViewMonth(prev => subMonths(prev, 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 dark:text-zinc-400 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="text-[0.75rem] font-bold text-zinc-700 dark:text-zinc-200 tracking-wide">
            {format(viewMonth, 'MMMM yyyy')}
          </span>
          <button
            type="button"
            onClick={() => setViewMonth(prev => addMonths(prev, 1))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 dark:text-zinc-400 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 px-2 pt-2 pb-0">
          {WEEKDAY_LABELS.map(lbl => (
            <div key={lbl} className="text-center text-[0.6rem] font-bold text-zinc-400 dark:text-zinc-500 pb-1 uppercase">
              {lbl}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-0.5 px-2 pb-2">
          {allDays.map(day => {
            const key          = format(day, 'yyyy-MM-dd');
            const inMonth      = isSameMonth(day, viewMonth);
            const isOrigin     = originKey === key;
            const isSelected   = selectedSet.has(key);
            const isTodayDay   = isToday(day);

            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(day)}
                disabled={isOrigin}
                title={isOrigin ? 'Original event date' : format(day, 'MMMM d, yyyy')}
                className={cn(
                  'relative h-7 w-full rounded-lg text-[0.7rem] font-semibold transition-all duration-100 select-none',
                  !inMonth && 'opacity-25',
                  isOrigin
                    ? 'bg-violet-600 text-white cursor-default ring-2 ring-violet-400 ring-offset-1 dark:ring-offset-zinc-900'
                    : isSelected
                      ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                      : isTodayDay
                        ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                        : inMonth
                          ? 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                          : 'text-zinc-400 dark:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                )}
              >
                {format(day, 'd')}
                {isOrigin && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white opacity-70" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick-clear */}
      {count > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-[0.65rem] text-zinc-400 hover:text-red-500 transition-colors underline underline-offset-2"
        >
          Clear all selected dates
        </button>
      )}
    </div>
  );
}
